import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
    ActiveGridCellDto,
    AirspaceAuthorisationResponse,
    AirspaceCheckResponse,
    DateFormatService,
    DisplayableMissionDto,
    FieldError,
    MissionRoleDto,
    OperationAuthorisation,
    PersonRolesDto
} from '@flyfreely-portal-ui/flyfreely';
import { FormlyFieldConfig } from '@ngx-formly/core';
import moment from 'moment';
import { formatAuthorisationTime, formatCivilTwilight } from '../helpers';

export function findTimeValues(
    mission: DisplayableMissionDto,
    checkResponse: AirspaceCheckResponse,
    dateFormatService: DateFormatService
) {
    const timeZone =
        mission.location?.timeZone ??
        mission.timeZone ??
        checkResponse?.timeZone ??
        null;
    const now = moment(new Date()).utc().toISOString();

    const startTime = formatAuthorisationTime(
        mission.missionDate,
        mission.timeZone
    );
    const validatorStart = moment
        .tz(mission.missionDate, mission.timeZone)
        .toISOString();

    const endTime = formatAuthorisationTime(
        moment(mission.missionDate)
            .add(mission.missionEstimatedTime, 'seconds')
            .toDate(),
        mission.timeZone
    );
    const validatorEnd = moment
        .tz(mission.missionDate, mission.timeZone)
        .add(mission.missionEstimatedTime, 'seconds')
        .toISOString();

    const civilTwilightStart = formatCivilTwilight(
        checkResponse?.sunsetAndRise?.civilTwilightStart,
        timeZone,
        dateFormatService
    );
    const validatorCivilTwilightStart = moment
        .tz(checkResponse?.sunsetAndRise?.civilTwilightStart, timeZone)
        .toISOString();
    const civilTwilightEnd = formatCivilTwilight(
        checkResponse?.sunsetAndRise?.civilTwilightEnd,
        timeZone,
        dateFormatService
    );
    const validatorCivilTwilightEnd = moment
        .tz(checkResponse?.sunsetAndRise?.civilTwilightEnd, timeZone)
        .toISOString();

    // Need start and end in a different format for duration calculations
    const start = moment.tz(mission.missionDate, mission.timeZone);
    const end = moment.tz(
        moment(mission.missionDate).add(
            mission.missionEstimatedTime,
            'seconds'
        ),
        mission.timeZone
    );
    const timeDiff = start.diff(moment.tz(now, timeZone), 'days');
    const duration = moment(end).diff(start, 'minutes');
    const hoursDiff = moment(end).diff(start, 'hours');
    const minutesDiff = duration - hoursDiff * 60;
    const startRangeString = `${timeDiff} Days from now`;
    const endRangeString = `${hoursDiff}Hr ${minutesDiff} Min Approval Window`;
    return {
        timeZone,
        startTime,
        validatorStart,
        endTime,
        validatorEnd,
        startRangeString,
        endRangeString,
        civilTwilightStart,
        validatorCivilTwilightStart,
        civilTwilightEnd,
        validatorCivilTwilightEnd
    };
}

export function findPilotValues(
    mission: DisplayableMissionDto,
    crp: PersonRolesDto
) {
    const currentJurisdiction = mission.location.airspaceJurisdiction;

    // Setup the RPIC details
    const rpic = mission.missionCrewDetails?.find(
        person =>
            person.role.coreRole === MissionRoleDto.CoreRole.PILOT_IN_COMMAND
    )?.person;
    const rpicPhone = rpic?.phoneNumber;
    const rpicArnAuth = rpic?.activeJurisdictions.find(
        j => j.id === currentJurisdiction.id
    );
    const rpicArn =
        rpicArnAuth != null ? rpicArnAuth.identifier?.identifier : '';
    const rpicAuthLabel =
        rpicArnAuth != null
            ? rpicArnAuth.identifier?.authorityType?.identifierLabel
            : 'Licence Number';

    // Setup the CRP details
    const crpArnAuth = crp?.activeJurisdictions?.find(
        j => j.id === currentJurisdiction.id
    );

    const crpArn = crpArnAuth != null ? crpArnAuth.identifier?.identifier : '';
    const crpAuthLabel =
        crpArnAuth != null
            ? crpArnAuth.identifier?.authorityType?.identifierLabel
            : 'Licence Number';
    return {
        rpic,
        rpicPhone,
        rpicArn,
        rpicAuthLabel,
        crp,
        crpArn,
        crpAuthLabel
    };
}

export function reduceToUniqueErrors(
    mergedErrors: { [key: string]: any },
    errorsToMerge: { [key: string]: any }
) {
    const keys = Object.keys(errorsToMerge);
    const newErrors = keys.filter(key => !mergedErrors[key]);
    const overlappingErrors = keys.filter(key => mergedErrors[key]);
    const overlappingErrorsToMerge = overlappingErrors.filter(
        key =>
            errorsToMerge[key] !== mergedErrors[key] &&
            errorsToMerge[key] !== null
    );
    const keysToMerge = newErrors.concat(overlappingErrorsToMerge);
    return {
        ...mergedErrors,
        ...keysToMerge.reduce(
            (acc, k) => ({ ...acc, [k]: errorsToMerge[k] }),
            {}
        )
    };
}

export function combineAuthorisationErrors(
    authorisation: AirspaceAuthorisationResponse
) {
    if (
        authorisation == null ||
        ((authorisation.errors == null ||
            Object.keys(authorisation.errors).length === 0) &&
            authorisation.authorisationList.filter(
                a => a.errors != null && Object.keys(a.errors).length > 0
            ).length === 0)
    ) {
        return {};
    }
    const mainErrors = authorisation.errors;
    const listErrors = authorisation.authorisationList
        .map(a => a?.errors)
        .reduce(
            (acc, errors) =>
                errors != null ? reduceToUniqueErrors(acc, errors) : acc,
            {}
        );
    return reduceToUniqueErrors(mainErrors, listErrors);
}

/**
 * Like `combineAuthorisationErrors`, but merges the mission/request-level
 * `authorisation.errors` with the errors of a single `OperationAuthorisation`
 * entry (e.g. the flight area whose GCD/Airspace tab is currently active)
 * rather than every entry in `authorisationList`. This keeps mission-level
 * validators (RPIC, delegated authority, times) working the same regardless
 * of which flight area's tab is active, while GCD/airspace-specific
 * validators reflect only the active flight area's own errors.
 */
export function combineAuthorisationErrorsForOperation(
    authorisation: AirspaceAuthorisationResponse,
    operation: OperationAuthorisation | null | undefined
) {
    if (
        authorisation == null ||
        ((authorisation.errors == null ||
            Object.keys(authorisation.errors).length === 0) &&
            (operation?.errors == null ||
                Object.keys(operation.errors).length === 0))
    ) {
        return {};
    }
    const mainErrors = authorisation.errors ?? {};
    return operation?.errors != null
        ? reduceToUniqueErrors(mainErrors, operation.errors)
        : mainErrors;
}

/**
 * Combines every entry in a preview's `authorisationList` (one per flight
 * area requested, or one per authorisation window for a longer mission)
 * into a single pseudo-`OperationAuthorisation`, so GCD/airspace/error
 * parsing considers every entry.
 * - `activeGridCellList` is the union of every entry's grid cells,
 *   deduplicated by `cellId`.
 * - `errors` is every entry's errors merged together (the same merge
 *   `combineAuthorisationErrors` uses across the whole list).
 * - `maximumHeight` is the most restrictive (lowest) ceiling across every
 *   entry, since the mission as a whole must satisfy all of them. This should
 *   be redundant as all authorisations will have the same height, but there
 *   are conceivable ways in which different heights can be achieved, plus this
 *   future-proofs the authorisations.
 * Fields with no sensible combined value (`id`, `controllingAuthority`,
 * `status`, `validity`, `operatingArea`, `startTime`, `endTime`,
 * `authorisationEligibility`, etc.) are taken from the first entry — they're
 * not read anywhere error/GCD/airspace parsing cares about.
 */
export function combineOperationAuthorisationList(
    authorisationList: OperationAuthorisation[] | null | undefined
): OperationAuthorisation | null {
    if (authorisationList == null || authorisationList.length === 0) {
        return null;
    }
    if (authorisationList.length === 1) {
        return authorisationList[0];
    }

    const activeGridCellList = Array.from(
        authorisationList
            .flatMap(a => a?.activeGridCellList ?? [])
            .reduce(
                (acc, cell) => acc.set(cell.cellId, cell),
                new Map<string, ActiveGridCellDto>()
            )
            .values()
    );

    const maximumHeights = authorisationList
        .map(a => a?.maximumHeight)
        .filter(h => h != null);
    const maximumHeight =
        maximumHeights.length > 0 ? Math.min(...maximumHeights) : null;

    const errors = authorisationList.reduce(
        (acc, a) =>
            a?.errors != null ? reduceToUniqueErrors(acc, a.errors) : acc,
        {} as { [key: string]: FieldError }
    );

    return {
        ...authorisationList[0],
        activeGridCellList,
        maximumHeight,
        errors
    };
}

/**
 * A function to find the earliest start time among all authorisations.
 */
function findAuthorisationStartTime(
    authorisation: AirspaceAuthorisationResponse
) {
    if (authorisation.authorisationList.length === 0) {
        return null;
    }
    return authorisation.authorisationList
        .map(a => a.startTime)
        .reduce((acc, start) =>
            acc == null || moment(start).isBefore(moment(acc)) ? start : acc
        );
}

/**
 * A function to find the latest end time among all authorisations.
 */
function findAuthorisationEndTime(
    authorisation: AirspaceAuthorisationResponse
) {
    if (authorisation.authorisationList.length === 0) {
        return null;
    }
    return authorisation.authorisationList
        .map(a => a.endTime)
        .reduce((acc, end) =>
            acc == null || moment(end).isAfter(moment(acc)) ? end : acc
        );
}

export function removeWhiteSpace(string: string) {
    if (string == null) {
        return '';
    }
    return string.replace(/\s/g, '');
}

/**
 * Builds a FormGroup from a FormlyFieldConfig array.
 *
 * NOTE: props.required on formly fields generated by FormlyJsonschema is set
 * via a runtime expression, not as a static value, so it cannot be read at
 * form-build time. Pass requiredKeys (from the JSON schema's `required` array)
 * to correctly apply Validators.required to the appropriate controls.
 *
 * @param fields     The FormlyFieldConfig array to build controls from.
 * @param requiredKeys  Keys that should have Validators.required applied.
 * @param savedValues   Optional saved values to pre-populate controls.
 */
export function buildFormGroupFromFormlyFields(
    fields: FormlyFieldConfig[],
    requiredKeys: string[],
    savedValues?: Record<string, any>
): FormGroup {
    const controls = (fields ?? []).reduce((acc, field) => {
        const key = field.key as string;
        const isRequired = requiredKeys?.includes(key) ?? false;
        return {
            ...acc,
            [key]: new FormControl(
                savedValues?.[key] ?? undefined,
                isRequired ? Validators.required : undefined
            )
        };
    }, {} as Record<string, FormControl>);
    return new FormGroup(controls);
}

/**
 * Maps a FormlyFieldConfig array produced by FormlyJsonschema.toFieldConfig()
 * to the FlyFreely wrapper types.
 *
 * FormlyJsonschema assigns abstract type names derived from the JSON schema
 * (`'boolean'`, `'enum'`, `'string'`, etc.). These need to be translated to
 * the FlyFreely formly field types before the fields can be rendered.
 */
export function mapToFormlyConfig(
    initialGroup: FormlyFieldConfig[]
): FormlyFieldConfig[] {
    if (initialGroup == null) {
        return null;
    }

    const booleanToRadioGroup = (
        field: FormlyFieldConfig
    ): FormlyFieldConfig => ({
        ...field,
        type: 'radio',
        props: {
            ...field.props,
            options: [
                { value: true, name: 'Yes' },
                { value: false, name: 'No' }
            ]
        }
    });

    // FormlyJsonschema sets type to 'enum' for oneOf/enum schemas and populates
    // props.options as { value: <const>, label: <title> } entries.
    const enumToNgSelect = (field: FormlyFieldConfig): FormlyFieldConfig => ({
        ...field,
        type: 'ng-select',
        props: {
            ...field.props,
            labelProp: 'label',
            valueProp: 'value',
            columnSpan: 12,
            multiple: false,
            clearable: true
        }
    });

    const stringToInput = (field: FormlyFieldConfig): FormlyFieldConfig => ({
        ...field,
        type: 'input'
    });

    return initialGroup.map(field => {
        switch (field.type) {
            case 'boolean':
                return booleanToRadioGroup(field);
            case 'enum':
                return enumToNgSelect(field);
            case 'string':
                return stringToInput(field);
            default:
                return field;
        }
    });
}
